import { useCallback, useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { type AreaTableView, type AreaView } from '../../../../services/areas/areas.service';
import { clamp } from '../../core/pos-shell-page.helpers';
import { type CanvasInteraction, type ResizeHandle, type TableFormState, type TableLayoutState } from '../../core/pos-shell-page.types';

type UseTableCanvasInteractionParams = {
  areaMode: 'view' | 'edit';
  isManager: boolean;
  setActiveAreas: Dispatch<SetStateAction<AreaView[]>>;
  setSettingsAreas: Dispatch<SetStateAction<AreaView[]>>;
  setEditingTableId: Dispatch<SetStateAction<number | null>>;
  setTableForm: Dispatch<SetStateAction<TableFormState>>;
  setError: Dispatch<SetStateAction<string | null>>;
  interactionRef: MutableRefObject<CanvasInteraction | null>;
  isPersistingInteractionRef: MutableRefObject<boolean>;
  persistTableUpdate: (
    areaId: number,
    tableId: number,
    next: TableLayoutState,
    baseline?: TableLayoutState | null,
  ) => Promise<void>;
};

export function useTableCanvasInteraction({
  areaMode,
  isManager,
  setActiveAreas,
  setSettingsAreas,
  setEditingTableId,
  setTableForm,
  setError,
  interactionRef,
  isPersistingInteractionRef,
  persistTableUpdate,
}: UseTableCanvasInteractionParams) {
  const applyTableLayoutPreview = useCallback((areaId: number, tableId: number, next: TableLayoutState) => {
    const updater = (areas: AreaView[]) =>
      areas.map((area) => {
        if (area.id !== areaId) {
          return area;
        }

        return {
          ...area,
          tables: (Array.isArray(area.tables) ? area.tables : []).map((table) =>
            table.id === tableId
              ? {
                  ...table,
                  number: next.number,
                  shape: next.shape,
                  positionX: next.positionX,
                  positionY: next.positionY,
                  width: next.width,
                  height: next.height,
                }
              : table,
          ),
        };
      });

    setSettingsAreas((previous) => updater(previous));
    setActiveAreas((previous) => updater(previous));
  }, [setActiveAreas, setSettingsAreas]);

  const beginCanvasInteraction = useCallback((
    event: React.MouseEvent<HTMLElement>,
    areaId: number,
    table: AreaTableView,
    mode: 'move' | 'resize',
    handle?: ResizeHandle,
  ) => {
    if (!isManager || areaMode !== 'edit') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Resolve the canvas element relative to the drag origin. For a resize the
    // event target is a corner handle (child of the table), so the canvas is two
    // levels up; for a move the target is the table itself, so the canvas is its
    // direct parent. Relying on a class name here is fragile because the table
    // class is a hashed CSS-module name, not a stable selector.
    const target = event.currentTarget as HTMLElement;
    const canvas = mode === 'resize' ? target.parentElement?.parentElement : target.parentElement;
    const rect = canvas?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    interactionRef.current = {
      mode,
      areaId,
      tableId: table.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      canvasWidth: rect.width,
      canvasHeight: rect.height,
      handle,
      origin: {
        number: table.number,
        shape: table.shape,
        positionX: table.positionX,
        positionY: table.positionY,
        width: table.width,
        height: table.height,
      },
      preview: {
        number: table.number,
        shape: table.shape,
        positionX: table.positionX,
        positionY: table.positionY,
        width: table.width,
        height: table.height,
      },
    };
    setEditingTableId(table.id);
    setTableForm({
      number: table.number,
      shape: table.shape,
    });
    setError(null);
  }, [areaMode, interactionRef, isManager, setEditingTableId, setError, setTableForm]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || isPersistingInteractionRef.current) {
        return;
      }

      const dx = event.clientX - interaction.startClientX;
      const dy = event.clientY - interaction.startClientY;

      let next: TableLayoutState = {
        ...interaction.origin,
      };

      if (interaction.mode === 'move') {
        next = {
          ...next,
          positionX: clamp(
            interaction.origin.positionX + dx,
            0,
            Math.max(0, interaction.canvasWidth - interaction.origin.width),
          ),
          positionY: clamp(
            interaction.origin.positionY + dy,
            0,
            Math.max(0, interaction.canvasHeight - interaction.origin.height),
          ),
        };
      } else {
        const minSize = 40;
        const right = interaction.origin.positionX + interaction.origin.width;
        const bottom = interaction.origin.positionY + interaction.origin.height;

        if (interaction.handle === 'se') {
          next.width = clamp(
            interaction.origin.width + dx,
            minSize,
            Math.max(minSize, interaction.canvasWidth - interaction.origin.positionX),
          );
          next.height = clamp(
            interaction.origin.height + dy,
            minSize,
            Math.max(minSize, interaction.canvasHeight - interaction.origin.positionY),
          );
        }

        if (interaction.handle === 'ne') {
          next.width = clamp(
            interaction.origin.width + dx,
            minSize,
            Math.max(minSize, interaction.canvasWidth - interaction.origin.positionX),
          );
          next.positionY = clamp(interaction.origin.positionY + dy, 0, bottom - minSize);
          next.height = clamp(bottom - next.positionY, minSize, interaction.canvasHeight);
        }

        if (interaction.handle === 'sw') {
          next.positionX = clamp(interaction.origin.positionX + dx, 0, right - minSize);
          next.width = clamp(right - next.positionX, minSize, interaction.canvasWidth);
          next.height = clamp(
            interaction.origin.height + dy,
            minSize,
            Math.max(minSize, interaction.canvasHeight - interaction.origin.positionY),
          );
        }

        if (interaction.handle === 'nw') {
          next.positionX = clamp(interaction.origin.positionX + dx, 0, right - minSize);
          next.positionY = clamp(interaction.origin.positionY + dy, 0, bottom - minSize);
          next.width = clamp(right - next.positionX, minSize, interaction.canvasWidth);
          next.height = clamp(bottom - next.positionY, minSize, interaction.canvasHeight);
        }
      }

      interaction.preview = next;
      applyTableLayoutPreview(interaction.areaId, interaction.tableId, next);
    };

    const onMouseUp = async () => {
      const interaction = interactionRef.current;
      if (!interaction || isPersistingInteractionRef.current) {
        return;
      }

      isPersistingInteractionRef.current = true;
      try {
        await persistTableUpdate(
          interaction.areaId,
          interaction.tableId,
          interaction.preview,
          interaction.origin,
        );
      } catch (interactionError: unknown) {
        if (interactionError instanceof Error) {
          setError(interactionError.message);
        } else {
          setError('Failed saving table layout');
        }
      } finally {
        interactionRef.current = null;
        isPersistingInteractionRef.current = false;
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    applyTableLayoutPreview,
    interactionRef,
    isPersistingInteractionRef,
    persistTableUpdate,
    setError,
  ]);

  return {
    beginCanvasInteraction,
  };
}
